import { Component, Inject, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ConfirmationService,
  CountryConvertService,
  ErrorReferenceService,
  NotificationService,
  VTService,
} from '@odin/odin-core';
import {
  PaymentAuditEvent,
  ScheduleItem,
  ScheduleOccurrence,
  ScheduleOccurrenceListResponse,
} from '../transactions-layout/models/pebl-payment';
import { RoleGuardService } from 'libs/odin-authentication/src/lib/services/role-guard.service';
import { AccessGuardService } from 'libs/odin-authentication/src/lib/services/access-guard.service';

@Component({
  standalone: false,
  selector: 'odin-pebl-schedule-details',
  templateUrl: './pebl-schedule-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./pebl-schedule-details.component.scss'],
})
export class PeblScheduleDetailsComponent implements OnInit {
  public actionButtonLoading: boolean = false;

  public occurrences: ScheduleOccurrence[] = [];
  public occurrencesLoading: boolean = false;
  public occurrencesError: boolean = false;
  public occurrencesErrorText: string = '';
  public occurrencesToken: string = '';
  public occurrencesFetched: boolean = false;

  public expandedOccurrences = new Set<string>();
  public auditEvents: Record<string, PaymentAuditEvent[]> = {};
  public auditLoading: Record<string, boolean> = {};
  public auditErrors: Record<string, boolean> = {};
  public auditErrorTexts: Record<string, string> = {};

  private readonly errorReference = inject(ErrorReferenceService);

  constructor(
    @Inject(MAT_DIALOG_DATA) public schedule: ScheduleItem,
    public dialogRef: MatDialogRef<PeblScheduleDetailsComponent>,
    private notificationService: NotificationService,
    private vtService: VTService,
    private confirmationService: ConfirmationService,
    private accessService: AccessGuardService,
    private roleGuardService: RoleGuardService,
    private countryConvertService: CountryConvertService,
    private router: Router,
  ) {}

  public ngOnInit(): void {
    this.LoadOccurrences();
  }

  public LoadOccurrences(): void {
    if (this.occurrencesLoading) return;
    this.occurrencesLoading = true;
    this.occurrencesError = false;
    this.vtService
      .GetScheduleOccurrences(
        this.schedule.scheduleReference,
        this.occurrencesToken || undefined,
      )
      .subscribe(
        (response: ScheduleOccurrenceListResponse) => {
          this.occurrences = [
            ...this.occurrences,
            ...(response.occurrences ?? []),
          ];
          this.occurrencesToken = response.lastEvaluatedKey ?? '';
          this.occurrencesLoading = false;
          this.occurrencesFetched = true;
        },
        (error: unknown) => {
          this.occurrencesLoading = false;
          this.occurrencesFetched = true;
          this.occurrencesError = true;
          this.occurrencesErrorText = this.errorReference.formatMessage(
            'Unable to load the send history.',
            error,
          );
        },
      );
  }

  public get hasMoreOccurrences(): boolean {
    return this.occurrencesToken !== '';
  }

  public ToggleOccurrence(occurrence: ScheduleOccurrence): void {
    const key = occurrence.paymentReference;
    if (this.expandedOccurrences.has(key)) {
      this.expandedOccurrences.delete(key);
      return;
    }
    this.expandedOccurrences.add(key);
    if (this.auditEvents[key] === undefined && !this.auditLoading[key]) {
      this.LoadAudit(occurrence);
    }
  }

  public LoadAudit(occurrence: ScheduleOccurrence): void {
    const key = occurrence.paymentReference;
    this.auditLoading[key] = true;
    this.auditErrors[key] = false;
    this.vtService
      .GetOccurrenceAuditHistory(this.schedule.scheduleReference, key)
      .subscribe(
        (events: PaymentAuditEvent[]) => {
          // Backend returns oldest-first; show most recent attempt on top
          this.auditEvents[key] = [...(events ?? [])].reverse();
          this.auditLoading[key] = false;
        },
        (error: unknown) => {
          this.auditLoading[key] = false;
          this.auditErrors[key] = true;
          this.auditErrorTexts[key] = this.errorReference.formatMessage(
            'Unable to load attempts.',
            error,
          );
        },
      );
  }

  public auditFor(occurrence: ScheduleOccurrence): PaymentAuditEvent[] {
    return this.auditEvents[occurrence.paymentReference] ?? [];
  }

  public isExpanded(occurrence: ScheduleOccurrence): boolean {
    return this.expandedOccurrences.has(occurrence.paymentReference);
  }

  public occurrenceStatusClass(occurrence: ScheduleOccurrence): string {
    switch (occurrence.status) {
      case 'Authorised':
        return 'mon-text-success';
      case 'Cancelled':
      case 'Expired':
        return 'mon-text-danger';
      default:
        return '';
    }
  }

  public occurrenceStatusText(occurrence: ScheduleOccurrence): string {
    switch (occurrence.status) {
      case 'Authorised':
        return 'Paid';
      case 'Sent':
        return 'Unpaid';
      default:
        return occurrence.status || '-';
    }
  }

  public isAuditEventPaid(event: PaymentAuditEvent): boolean {
    return event.status === 'Authorised';
  }

  public auditEventIcon(event: PaymentAuditEvent): string {
    return this.isAuditEventPaid(event) ? 'check_circle' : 'cancel';
  }

  public auditEventClass(event: PaymentAuditEvent): string {
    return this.isAuditEventPaid(event)
      ? 'mon-text-success'
      : 'mon-text-danger';
  }

  public hasAuditEventDetails(event: PaymentAuditEvent): boolean {
    return !!event.crossReference;
  }

  public viewAuditEventTransaction(event: PaymentAuditEvent): void {
    if (!event.crossReference) return;
    this.dialogRef.close();
    this.router.navigate([`/transactions/detail/${event.crossReference}`]);
  }

  public GrantChecker(): boolean {
    return (
      this.accessService.hasNewGrant('txn:CreatePebl') ||
      this.roleGuardService.hasPermission('pbl:Create')
    );
  }

  public get currencyAlphaCode(): string {
    const countryData =
      this.countryConvertService.GetCountryByCurrencyNumericCode(
        this.schedule.currencyCode,
      );
    return countryData ? countryData.currencyCode : 'GBP';
  }

  public deliveryIcon(): string {
    return this.schedule.delivery === 'email' ? 'email' : 'phone_iphone';
  }

  public deliveryName(): string {
    return this.schedule.delivery === 'email' ? 'E-mail' : 'SMS';
  }

  public recipient(): string {
    switch (this.schedule.delivery) {
      case 'email':
        return this.schedule.emailAddress || '-';
      case 'sms':
        return this.schedule.phoneNumber || '-';
      default:
        return '-';
    }
  }

  public statusIcon(): string {
    switch (this.schedule.status) {
      case 'Completed':
        return 'check_circle';
      case 'Cancelled':
        return 'cancel';
      case 'Failed':
        return 'error';
      case 'Sending':
        return 'sync';
      default:
        return 'schedule';
    }
  }

  public statusBadgeClass(): string {
    switch (this.schedule.status) {
      case 'Completed':
        return 'mon-bg-success mon-text-success';
      case 'Cancelled':
      case 'Failed':
        return 'mon-bg-danger mon-text-danger';
      case 'Sending':
        return 'mon-bg-primary mon-text-primary';
      default:
        return 'mon-bg-warning mon-text-warning';
    }
  }

  public scheduleSummary(): string {
    if (this.schedule.type !== 'Recurring') return 'One-off send';
    const frequency = this.schedule.frequency
      ? this.schedule.frequency.charAt(0).toUpperCase() +
        this.schedule.frequency.slice(1)
      : '';
    return this.schedule.totalOccurrences
      ? `${frequency}, ${this.schedule.occurrencesSent} of ${this.schedule.totalOccurrences} sent`
      : `${frequency}, ${this.schedule.occurrencesSent} sent`;
  }

  public CancelSchedule(): void {
    this.notificationService.InputDialog(
      {
        placeholder: 'Reason',
        readonly: false,
        title: 'Cancellation Reason',
        subHeading: 'Provide a reason this schedule is being cancelled',
        defaultValue: '',
        allowCopy: false,
      },
      (reason: string) => {
        if (!reason || reason.trim().length === 0) {
          this.notificationService.SmallDialog(
            'Cancellation reason is required to cancel the schedule.',
            'Close',
            3000,
          );
          return;
        }

        this.confirmationService
          .ConfirmDecline(
            'Cancel Schedule',
            'Are you sure you want to cancel this schedule? No further links will be sent.',
            'Confirm',
            'Cancel',
            'primary',
            'warn',
          )
          .subscribe((confirmed: boolean) => {
            if (!confirmed) return;
            this.actionButtonLoading = true;
            this.vtService
              .CancelSchedule(this.schedule.scheduleReference, reason)
              .subscribe(
                () => {
                  this.actionButtonLoading = false;
                  this.dialogRef.close(true);
                },
                (error: HttpErrorResponse) => {
                  this.actionButtonLoading = false;
                  if (error.status === 409) {
                    this.notificationService.ErrorSmallDialog(
                      'This schedule can no longer be cancelled (it is not active).',
                      error,
                    );
                    this.dialogRef.close(true);
                    return;
                  }
                  this.notificationService.ErrorSmallDialog(
                    'Failed to cancel the schedule, please try again.',
                    error,
                  );
                },
              );
          });
      },
      {
        panelClass: ['main-panel'],
        maxWidth: '400px',
        autoFocus: false,
      },
    );
  }
}
